import { Link } from 'react-router-dom';
import { cx, fieldClass, IconCheck, labelClass, Wordmark } from '@timely/ui';
import { PASSWORD_RULES } from '../auth/authErrors';
import { titled, useDocumentTitle } from '../lib/documentTitle';
import { publicConfig } from '../config/env';

/**
 * The frame every signed-out screen sits in.
 *
 * Timely Payment is the only brand here. A company's own name and logo appear
 * once somebody is inside a company — never before, because at this point we do
 * not know which company they belong to, and guessing from an email domain
 * would be a way of telling a stranger who our customers are.
 */
export function AuthLayout({
    title,
    lede,
    children,
    footer,
}: {
    title: string;
    lede?: React.ReactNode;
    children: React.ReactNode;
    footer?: React.ReactNode;
}) {
    useDocumentTitle(titled(title));
    const site = marketingSite();
    return (
        // The page is the form's surface, to the foot of the screen on a phone too (the brand is a band above it).
        <div className="min-h-dvh lg:grid lg:grid-cols-12 bg-card">
            {/* The brand side: the navy room, the name, and one sentence — nothing put there only to fill it (no ruled grid,
                no ticked list). A band on a phone. */}
            <div className="relative bg-chrome text-chrome-label px-6 py-5 lg:col-span-5 lg:px-12 lg:py-12 lg:flex lg:flex-col">
                <a href={site} className="self-start rounded-control" aria-label="Timely Payment — the website">
                    <Wordmark onChrome className="h-5 lg:h-6" />
                </a>
                <div className="max-lg:hidden mt-auto pt-16 max-w-md">
                    <p className="font-display stretch-display text-display font-semibold leading-tight tracking-display text-balance">
                        Turn outstanding receivables into a daily collection workflow.
                    </p>
                    <p className="mt-6 text-body text-chrome-label-2 leading-relaxed max-w-sm">
                        A daily list of who needs attention and why, with every follow-up, promise and cheque kept on the account it
                        belongs to — read from TallyPrime or the Google Sheet you already keep.
                    </p>
                </div>
                <p className="max-lg:hidden mt-auto pt-12 text-caption text-chrome-label-3 max-w-md">
                    Free for now. Your accounting stays the record: Timely Payment reads it and never writes to it.
                </p>
            </div>

            {/* The form side is the page itself (2026-10 global sweep): the form on the surface, centred in the height
                it has, not a card pinned to the top of an empty half. The links out stay at the foot. */}
            <main className="flex flex-col items-center px-5 py-10 bg-card lg:col-span-7 lg:px-12 lg:py-12">
                <div className="w-full max-w-sm my-auto">
                    <h1 className="font-display stretch-title text-title font-semibold text-label tracking-title leading-tight">
                        {title}
                    </h1>
                    {lede && (
                        <p className="text-body text-label-2 mt-2.5 leading-relaxed">{lede}</p>
                    )}
                    <div className="mt-7">{children}</div>
                    {footer && <div className="text-body text-label-2 mt-8">{footer}</div>}
                </div>
                <nav aria-label="About Timely Payment" className="pt-10 flex flex-wrap justify-center gap-x-5 gap-y-1 text-secondary text-label-3">
                    <a href={site} className="py-1.5 hover:text-label hover:underline underline-offset-4">timelypayment.in</a>
                    <a href={`${site}/privacy`} className="py-1.5 hover:text-label hover:underline underline-offset-4">Privacy</a>
                    <a href={`${site}/terms`} className="py-1.5 hover:text-label hover:underline underline-offset-4">Terms</a>
                </nav>
            </main>
        </div>
    );
}

/** The public site, for the links out of the signed-out screens; the real domain if the build names none. */
function marketingSite(): string {
    try { return publicConfig().marketingUrl.replace(/\/+$/, ''); } catch { return 'https://timelypayment.in'; }
}

/** A labelled field. The label is a real one, not a placeholder standing in. */
export function Field({
    label,
    hint,
    error,
    large = false,
    ...input
}: React.InputHTMLAttributes<HTMLInputElement> & {
    /** 44px, for the signed-out screens, where the page is the form (DESIGN-SYSTEM §6–7); 36px everywhere else. */
    large?: boolean;
    label: string;
    hint?: React.ReactNode;
    error?: string | null;
}) {
    const id = input.id ?? input.name ?? label.toLowerCase().replace(/\s+/g, '-');
    const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null]
        .filter(Boolean)
        .join(' ');
    return (
        <div className="mb-4">
            <label htmlFor={id} className={labelClass}>
                {label}
            </label>
            <input
                {...input}
                id={id}
                aria-invalid={error ? true : undefined}
                aria-describedby={describedBy || undefined}
                className={fieldClass({ size: large ? 'lg' : 'md', invalid: Boolean(error), className: cx('w-full', input.className) })}
            />
            {hint && (
                <div id={`${id}-hint`} className="text-secondary text-label-3 mt-1.5 leading-snug">
                    {hint}
                </div>
            )}
            {error && (
                <div id={`${id}-error`} className="text-secondary text-dang mt-1.5" role="alert">
                    {error}
                </div>
            )}
        </div>
    );
}

/** The password rules, stated before anybody types and ticked off as each is met. */
export function PasswordRules({ problems }: { problems: readonly string[] }) {
    return (
        <ul className="space-y-0.5" aria-label="Password rules">
            {PASSWORD_RULES.map(rule => {
                const met = !problems.includes(rule);
                return (
                    <li key={rule} className={cx('flex items-center gap-1.5 transition-colors duration-150', met ? 'text-pos' : 'text-label-3')} data-met={met ? 'yes' : 'no'}>
                        {/* A rule not yet met is a list's bullet, not an empty circle that reads as a radio button. */}
                        <span className="size-3.5 grid place-items-center flex-none" aria-hidden="true">
                            {met ? <IconCheck /> : <span className="size-1 rounded-full bg-current" />}
                        </span>
                        <span>{rule}</span>
                        <span className="sr-only">{met ? '(met)' : '(not yet met)'}</span>
                    </li>
                );
            })}
        </ul>
    );
}

/** A message that concerns the whole form rather than one field. */
export function FormNotice({ tone, children }: { tone: 'error' | 'ok'; children: React.ReactNode }) {
    return (
        <div
            role={tone === 'error' ? 'alert' : 'status'}
            className={cx(
                'rounded-control px-3.5 py-3 text-body leading-relaxed mb-4',
                tone === 'error' ? 'bg-dang-bg text-dang' : 'bg-pos-bg text-pos',
            )}
        >
            {children}
        </div>
    );
}

export const AuthLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
    <Link to={to} className="text-accent-text font-semibold hover:underline underline-offset-4">
        {children}
    </Link>
);
